import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {
  EarningsData,
  History,
  EarningsInfo,
} from 'src/types/referrer-portal';
import {useDispatch, useSelector} from 'react-redux';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {
  getFormattedDate,
  getDateRangeOfDaysDifference,
  isReferrerPortal,
  formatAmount,
} from 'src/components/referral-v2/common';
import {getTimeAgoOptions} from 'src/utils/referral-v2';
import {
  getUserEarnings,
  getEarningsGraphData,
} from 'src/action-creators/referrer-portal/earnings';
import {selectEarnings} from 'src/selectors/referrer-portal';
import {
  Dropdown,
  BodySmall,
  KPIBox,
  Icon,
  Card,
  CardHeader,
  CardContent,
  SubTitleSmall,
  SubTitleExtraSmall,
  CircularLoader,
  EmptyState,
  Chip,
} from '@spaced-out/ui-design-system/lib/components/index';
import EarningsGraph from 'src/components/referrer-portal/earnings/earnings-graph';
import EarningTabs from 'src/components/referrer-portal/earnings/earnings-tabs';
import css from './earnings.css';


const Earnings = () => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const timeAgoOptions = getTimeAgoOptions(t);

  const [timeFrame, setTimeFrame] = React.useState<string>('30');

  const isEarningsDataLoading = useAsyncDependency(() => {
    const query = getDateRangeOfDaysDifference(timeFrame);
    return Promise.all([
      dispatch(getUserEarnings(query)),
      dispatch(getEarningsGraphData(query)),
    ]);
  }, [timeFrame]);

  const isEarningsGraphDataLoading = useAsyncDependency(() => {
    const query = getDateRangeOfDaysDifference('Last year');
    return dispatch(getEarningsGraphData(query));
  }, []);

  const earningsData: EarningsData | null | undefined =
    useSelector(selectEarnings);

  const selectedDateFilter =
    timeAgoOptions.find((option) => option.key === timeFrame)?.label ?? '';

  const renderKPIBox = (topContent: string, amountKey: string) => (
    <KPIBox
      semantic="neutral"
      classNames={{wrapper: css.cardWidth}}
      topContent={topContent}
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'EarningsData'.
      middleContent={formatAmount(earningsData?.[amountKey]?.amount)}
      bottomContent={
        <EarningsStatusInfo
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'EarningsData'.
          earningsInfo={earningsData?.[amountKey]}
          selectedDateFilter={selectedDateFilter}
        />
      }
    />
  );
  return (
    <div
      className={
        isReferrerPortal()
          ? css.referrerPortalEarningsContainer
          : css.myEarningsContainer
      }
    >
      <div className={css.myEarningsHeader}>
        {!isReferrerPortal() && <EarningTabs />}
        <Dropdown
          menu={{
            options: timeAgoOptions,
          }}
          dropdownInputText={
            timeAgoOptions.find((option) => option.key === timeFrame)?.label
          }
          classNames={{
            wrapper: css.timeframeFilter,
          }}
          onChange={(option) => setTimeFrame(option.key)}
          // @ts-expect-error - TS2769 - No overload matches this call.
          selectedKeys={[timeFrame]}
        />
      </div>

      {isEarningsGraphDataLoading || isEarningsDataLoading ? (
        <CircularLoader className={css.loader} />
      ) : (
        <>
          <div className={css.earningsStatus}>
            {renderKPIBox(
              t('Earnings_by_Referral_Request', 'Earnings by Referral Request'),
              'referral_request',
            )}
            {renderKPIBox(
              t(
                'Earnings_by_Direct_Submission',
                'Earnings by Direct Submission',
              ),
              'direct_submission',
            )}
            {renderKPIBox(
              t('Pending_Payouts', 'Pending Payouts'),
              'pending_payouts',
            )}
            {renderKPIBox(
              t('Total_Earnings', 'Total Earnings'),
              'total_earnings',
            )}
          </div>
          <div className={css.earningsContainer}>
            <Card classNames={{wrapper: css.earningGraphCard}}>
              <CardHeader>
                {new Date().getFullYear()}{' '}
                {t('Earnings_Graph', 'Earnings Graph')}
              </CardHeader>
              <CardContent className={css.graphContent}>
                <div className={css.paymentTab}>
                  <SubTitleExtraSmall>
                    {t('This_Year', 'This Year')}
                  </SubTitleExtraSmall>
                  <div className={css.defaultGap}>
                    <Chip showStatusIndicator={true} semantic="success">
                      {t('My_Earnings', 'My Earnings')}
                    </Chip>
                    <Chip showStatusIndicator={true} semantic="information">
                      {t('Median_Earnings', 'Median Earnings')}
                    </Chip>
                  </div>
                </div>
                <EarningsGraph />
              </CardContent>
            </Card>
            <EarningsHistory history={earningsData?.history} />
          </div>
        </>
      )}
    </div>
  );
};

export default Earnings;

const EarningsStatusInfo = ({
  earningsInfo,
  selectedDateFilter,
}: {
  earningsInfo: EarningsInfo | null | undefined;
  selectedDateFilter: string;
}) => {
  const {t} = useLabelTranslation();
  if (!earningsInfo || earningsInfo.change_type === 'no_data') {
    return null;
  }
  const {percentage_change, change_type} = earningsInfo;
  return (
    <div className={css.cardTextRow}>
      {change_type === 'decrease' && (
        <Icon color="danger" name="arrow-trend-down" />
      )}
      {change_type === 'increase' && (
        <Icon color="success" name="arrow-trend-up" />
      )}
      {change_type === 'no_change' && (
        <Icon color="success" name="arrow-right" />
      )}
      <BodySmall className={css.cardText}>
        {percentage_change} % (
        {change_type !== 'no_change'
          ? t('Compared_to', 'Compared to')
          : t('Same_as', 'Same as')}{' '}
        {selectedDateFilter.toLowerCase() || ''})
      </BodySmall>
    </div>
  );
};

const EarningsHistory = ({
  history = [],
}: {
  history: History[] | null | undefined;
}) => {
  const {t, language} = useLabelTranslation();
  return (
    <Card classNames={{wrapper: css.historyCard}}>
      <CardHeader>{t('Earnings_History', 'Earnings History')}</CardHeader>
      <CardContent className={css.historyContent}>
        {history && history.length === 0 && (
          <EmptyState
            classNames={{wrapper: css.emptyState}}
            description={`${t(
              'The_data_will_be_displayed_once_the_history_is_available',
              'The data will be displayed once the history is available',
            )}.`}
            imageVariant="data"
            title={`${t(
              'No_earnings_history_found',
              'No earnings history found',
            )}.`}
          />
        )}
        {history &&
          history.length > 0 &&
          history.map((historyData, index) => {
            const {amount, candidate, job_title, payment_date} = historyData;
            const isLastElement = history.length - 1 === index;
            return (
              <>
                <BodySmall className={css.history} key={index}>
                  {candidate}
                  {` ${t('was', 'was')}`}
                  <SubTitleSmall>
                    {`${t('hired_as_a', 'hired as a')} `}
                    {job_title}
                  </SubTitleSmall>
                  {` ${t('and', 'and')} `}
                  <SubTitleSmall>
                    {formatAmount(parseInt(amount, 0))}
                  </SubTitleSmall>
                  {` ${t('Credited_on', 'Credited on')} `}
                  {getFormattedDate(payment_date, language)}
                </BodySmall>
                {!isLastElement && <div className={css.dividerLine} />}
              </>
            );
          })}
      </CardContent>
    </Card>
  );
};
